import { useEffect, useState } from "react";
import { UserCircleGear, ArrowRight, HandPalm, CheckCircle, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav } from "../components/cc/common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function ResolveChangeDialog({ change, onClose, onResolved }) {
  const [note, setNote] = useState("");
  const [releaseHold, setReleaseHold] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); setReleaseHold(true); }, [change?.id]);
  if (!change) return null;

  const resolve = async () => {
    setBusy(true);
    try {
      await api.put(`/commission/producer-changes/${change.id}/resolve`, { note, release_hold: releaseHold });
      toast.success(releaseHold ? "Change resolved — hold released" : "Change resolved — hold kept in place");
      onClose();
      onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!change} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cc-resolve-change-dialog">
        <DialogHeader><DialogTitle>Resolve assignment change</DialogTitle></DialogHeader>
        <div className="text-sm bg-slate-50 rounded p-3 space-y-1">
          <p className="font-mono text-xs">{change.policy_number} <span className="font-sans text-slate-500">· {change.insured_name || "—"}</span></p>
          <p className="flex items-center gap-1.5">{change.old_producer_raw} <ArrowRight size={14} className="text-slate-400" /> <strong>{change.new_producer_raw}</strong></p>
          <p className="text-xs text-slate-400">Detected {fmtDate(change.detected_at)} · snapshot {change.batch_filename}</p>
        </div>
        <Textarea placeholder="Why is this change legitimate (or not)? Required, min 5 chars" value={note}
          onChange={(e) => setNote(e.target.value)} data-testid="cc-change-note-input" />
        <div className="flex items-center gap-2">
          <Switch checked={releaseHold} onCheckedChange={setReleaseHold} data-testid="cc-release-hold-switch" />
          <span className="text-sm">{releaseHold ? "Release the payment hold" : "Keep the payment hold in place"}</span>
        </div>
        <Button onClick={resolve} disabled={note.trim().length < 5 || busy} data-testid="cc-resolve-change-btn">
          <CheckCircle size={15} className="mr-1" /> Resolve change
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ProducerActionsDialog({ producer, producers, onClose, onDone }) {
  const [mode, setMode] = useState("status");
  const [note, setNote] = useState("");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode("status"); setNote(""); setTarget(""); }, [producer?.id]);
  if (!producer) return null;
  const isFormer = producer.status === "former";

  const setStatus = async () => {
    setBusy(true);
    try {
      await api.put(`/commission/producers/${producer.id}/status`, { status: isFormer ? "active" : "former", note });
      toast.success(isFormer ? `${producer.name_raw} reactivated` : `${producer.name_raw} marked as former — their commissions will be retained by the agency`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/commission/producers/${producer.id}/merge`, { target_id: target, note });
      toast.success(`Merged — ${data.policies_repointed} policies re-pointed; '${producer.name_raw}' kept as alias`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!producer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cc-producer-actions-dialog">
        <DialogHeader><DialogTitle>{producer.name_raw}</DialogTitle></DialogHeader>
        <div className="flex gap-1">
          {["status", "merge"].map((t) => (
            <button key={t} onClick={() => setMode(t)} data-testid={`cc-producer-mode-${t}`}
              className={`px-3 py-1 rounded-full text-xs font-medium ${mode === t ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>
              {t === "status" ? (isFormer ? "Reactivate" : "Mark as former") : "Merge duplicate"}
            </button>
          ))}
        </div>
        {mode === "status" ? (
          <>
            <p className="text-xs text-slate-500">
              {isFormer
                ? `Reactivating lets ${producer.name_raw} earn commissions again.`
                : `Former producers stay as the Assigned Producer on their policies for reporting accuracy, but calculated commissions and chargebacks are retained/absorbed by the agency (shown as "house" lines).`}
            </p>
            <Textarea placeholder={isFormer ? "Note (optional)" : "Note (required, min 5 chars) — e.g. left agency 9/2026"}
              value={note} onChange={(e) => setNote(e.target.value)} data-testid="cc-producer-status-note" />
            <Button disabled={busy || (!isFormer && note.trim().length < 5)} onClick={setStatus} data-testid="cc-producer-status-btn">
              {isFormer ? "Reactivate producer" : "Mark as former (agency retains commissions)"}
            </Button>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              Same person, two spellings? Merge <strong>{producer.name_raw}</strong> into the surviving producer —
              all policies re-point and future EZLynx syncs with this spelling attach to the survivor. Active rules
              on this producer must be revoked first.
            </p>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="cc-producer-merge-target"><SelectValue placeholder="Merge into…" /></SelectTrigger>
              <SelectContent>
                {producers.filter((p) => p.id !== producer.id).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name_raw}{p.user_name ? ` → ${p.user_name}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea placeholder="Note (required, min 5 chars) — e.g. Tony Gable = Anthony Gable" value={note}
              onChange={(e) => setNote(e.target.value)} data-testid="cc-producer-merge-note" />
            <Button disabled={busy || !target || note.trim().length < 5} onClick={merge} data-testid="cc-producer-merge-btn">
              Merge producers (audited)
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CommissionProducersPage() {
  const { hasPerm } = useAuth();
  const [producers, setProducers] = useState([]);
  const [changes, setChanges] = useState([]);
  const [members, setMembers] = useState([]);
  const [changeFilter, setChangeFilter] = useState("open");
  const [active, setActive] = useState(null);
  const [actionsFor, setActionsFor] = useState(null);
  const canRules = hasPerm("commission.rules");
  const canResolve = hasPerm("commission.resolve");

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get("/commission/producers"),
      api.get(`/commission/producer-changes${changeFilter !== "all" ? `?status=${changeFilter}` : ""}`),
    ]);
    setProducers(p.data);
    setChanges(c.data);
  };
  useEffect(() => { load().catch(() => {}); }, [changeFilter]);
  useEffect(() => {
    if (canRules) api.get("/users").then((r) => setMembers(r.data.filter((u) => u.is_active !== false))).catch(() => {});
  }, [canRules]);

  const link = async (producer, userId) => {
    try {
      await api.put(`/commission/producers/${producer.id}/link`, { user_id: userId === "__none__" ? null : userId });
      toast.success(userId === "__none__" ? `${producer.name_raw} unlinked` : "Producer linked");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    }
  };

  const unlinked = producers.filter((p) => !p.user_id).length;
  const openHolds = changes.filter((c) => c.status === "open").length;

  return (
    <div data-testid="commission-producers-page">
      <div className="mb-2">
        <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
          <UserCircleGear size={26} weight="duotone" className="text-indigo-600" /> Producer attribution
        </h1>
        <p className="text-sm text-slate-500 mt-1">EZLynx Assigned Producer is the attribution source of record — assignment changes trigger payment holds until a human resolves them.</p>
      </div>
      <CCNav />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-medium text-sm flex items-center gap-1.5">
              <HandPalm size={16} className="text-amber-600" /> Assignment changes
              {openHolds > 0 && <Badge className="bg-red-100 text-red-700">{openHolds} open hold{openHolds > 1 ? "s" : ""}</Badge>}
            </h2>
            <div className="flex gap-1">
              {["open", "resolved", "all"].map((f) => (
                <button key={f} onClick={() => setChangeFilter(f)} data-testid={`cc-changes-filter-${f}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${changeFilter === f ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          {changes.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">{changeFilter === "open" ? "No open assignment changes — every policy's producer matches the latest snapshot." : "Nothing here."}</p>
          ) : (
            <div className="divide-y divide-slate-50" data-testid="cc-changes-list">
              {changes.map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-mono text-xs">{c.policy_number} <span className="font-sans text-slate-500">· {c.insured_name || "—"}</span></p>
                    <p className="text-sm flex items-center gap-1.5 mt-0.5">
                      {c.old_producer_raw} <ArrowRight size={13} className="text-slate-400" /> <strong>{c.new_producer_raw}</strong>
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {fmtDate(c.detected_at)} · {c.batch_filename}
                      {c.status === "resolved" && <> · resolved by {c.resolved_by_name}: {c.resolution_note}</>}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {c.status === "open"
                      ? <Badge className="bg-red-100 text-red-700">{c.hold ? "payment hold" : "open"}</Badge>
                      : <Badge className={c.hold ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>{c.hold ? "resolved · hold kept" : "resolved"}</Badge>}
                    {c.status === "open" && canResolve && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setActive(c)} data-testid={`cc-resolve-change-${c.id}`}>Resolve</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-medium text-sm flex items-center gap-1.5"><LinkSimple size={16} /> Producers from EZLynx</h2>
            {unlinked > 0 && <Badge className="bg-amber-100 text-amber-800">{unlinked} unlinked</Badge>}
          </div>
          {producers.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">No producers yet — they appear automatically when EZLynx snapshots validate.</p>
          ) : (
            <table className="w-full text-sm" data-testid="cc-producers-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-2">EZLynx name</th>
                  <th className="px-4 py-2">Team member</th>
                  <th className="px-4 py-2 text-right">Policies</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {producers.map((p) => (
                  <tr key={p.id} className={`border-b border-slate-50 ${p.status === "former" ? "opacity-70" : ""}`}>
                    <td className="px-4 py-2.5 font-medium">
                      {p.name_raw}
                      {p.status === "former" && <Badge className="bg-slate-200 text-slate-600 ml-1.5">former · house</Badge>}
                      {p.alias_names?.length > 0 && (
                        <p className="text-[11px] font-normal text-slate-400">aka {p.alias_names.join(", ")}</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {canRules ? (
                        <Select value={p.user_id || "__none__"} onValueChange={(v) => link(p, v)}>
                          <SelectTrigger className="h-8 w-48" data-testid={`cc-producer-link-${p.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__"><span className="text-slate-400">Not linked</span></SelectItem>
                            {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        p.user_name || <Badge className="bg-amber-100 text-amber-800">unlinked</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">{p.policy_count}</td>
                    <td className="px-4 py-2.5 text-right">
                      {canRules && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500"
                          onClick={() => setActionsFor(p)} data-testid={`cc-producer-actions-${p.id}`}>Manage</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-slate-400 px-4 py-3">Linking tells the rule engine (next phase) which team member earns commission for a producer's policies. Links are audited.</p>
        </div>
      </div>

      <ResolveChangeDialog change={active} onClose={() => setActive(null)} onResolved={load} />
      <ProducerActionsDialog producer={actionsFor} producers={producers} onClose={() => setActionsFor(null)} onDone={load} />
    </div>
  );
}
